import React from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router';
import { createSelector } from 'reselect';

import { Loader } from '@opendoor/bricks';
import * as sessionActions from '@opendoor/shared-fe/build/store/session/actions';
import * as sessionSelectors from '@opendoor/shared-fe/build/store/session/selectors';
import { trackEvent } from '../../api/analytics';
import AccountLoginForm from '../../components/account/AccountLoginForm';
import { IRequestStatus } from '../../declarations';
import { REDIRECT_URLS_REGEX } from '../../helpers/globals';
import parseQueryString from '../../helpers/parseQueryString';
import layoutWrapper from '../../hocs/layoutWrapper';

import 'LoginPageStyle.less';

export interface IQueryParams {
  config?: 'resaleClosingDashboard';
  email?: string;
  redirectTo?: string;
}

export interface ILoginPageProps extends RouteComponentProps<{}> {
  loading?: boolean;
  loggedIn: boolean;
  logIn: (email: string, password: string, rememberMe: boolean) => void;
  loginRequestStatus: IRequestStatus;
  sessionInfoIsLoading: boolean;
  serverLoginRedirect: string | undefined;
}

export class LoginPage extends React.PureComponent<ILoginPageProps> {
  public componentDidMount() {
    trackEvent('login', 'load', undefined, this.getQueryParams());
    this.redirectIfLoggedIn();
  }

  public componentWillReceiveProps(nextProps: ILoginPageProps) {
    this.redirectIfLoggedIn(nextProps);
  }

  public redirectIfLoggedIn(nextProps?: ILoginPageProps) {
    const { loggedIn, history, serverLoginRedirect } = nextProps || this.props;
    if (loggedIn) {
      const queryParams = this.getQueryParams();
      trackEvent('login', 'redirect', undefined, {
        ...queryParams,
        serverLoginRedirect,
      });

      if (!queryParams.redirectTo && serverLoginRedirect && serverLoginRedirect !== '/') {
        window.location.assign(serverLoginRedirect);
      } else {
        // allow redirection to any opendoor.com url (and only opendoor.com urls)
        if (REDIRECT_URLS_REGEX.test(queryParams.redirectTo)) {
          window.location.assign(queryParams.redirectTo);
        } else {
          history.push(queryParams.redirectTo || '/');
        }
      }
    }
  }

  public getQueryParams(): IQueryParams {
    const { location } = this.props;
    return parseQueryString(location.search);
  }

  public render(): JSX.Element {
    const { logIn, loginRequestStatus, sessionInfoIsLoading } = this.props;
    const { config, email } = this.getQueryParams();

    if (sessionInfoIsLoading) {
      return <Loader />;
    }

    let headerText: string | undefined;
    switch (config) {
      case 'resaleClosingDashboard':
        headerText = 'Sign in to get back to your dashboard';
      default:
        break;
    }

    return (
      <div className="login-page">
        <AccountLoginForm
          email={email}
          headerText={headerText}
          loginError={loginRequestStatus.error}
          loading={loginRequestStatus.isLoading}
          onSubmit={logIn}
        />
      </div>
    );
  }
}

const mapStateToProps = createSelector(
  [
    sessionSelectors.loggedIn,
    sessionSelectors.loginRequestStatus,
    sessionSelectors.infoIsLoading,
    sessionSelectors.serverLoginRedirect,
  ],
  (
    loggedIn: boolean,
    loginRequestStatus: IRequestStatus,
    sessionInfoIsLoading: boolean,
    serverLoginRedirect: string | undefined,
  ) => ({
    loggedIn,
    loginRequestStatus,
    serverLoginRedirect,
    sessionInfoIsLoading,
  }),
);

const mapDispatchToProps = {
  logIn: sessionActions.logIn,
};

export default layoutWrapper(
  withRouter(
    connect(
      mapStateToProps,
      mapDispatchToProps,
    )(LoginPage),
  ),
  { footerType: 'NONE' },
);
